import axios from 'axios';

import { publicApiBase } from './customHost';

const BACKEND_URL = publicApiBase();

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  // NOTE: Do NOT set withCredentials: true. We authenticate via Bearer tokens
  // (Authorization header) stored in localStorage, not cookies. Setting it
  // triggers "credentials mode: include" which conflicts with the Emergent
  // ingress proxy's `Access-Control-Allow-Origin: *` and breaks all API calls
  // on custom domains (e.g. veluro.ai) in production.
});

// Which screen triggered an AI-credit charge — shown in Settings → Billing → Credit history.
const SURFACES = [['/vela', 'Vela'], ['/home', 'Vela'], ['/chat', 'Veluro Chat'], ['/campaigns', 'AI Campaign Builder'], ['/social', 'Social'], ['/funnels', 'Funnels'],
  ['/landing', 'Landing Pages'], ['/seo', 'SEO Engine'], ['/growth', 'Growth Plans'], ['/decks', 'Pitch Decks'], ['/blog', 'Blog'], ['/leads', 'Leads'],
  ['/sales-coach', 'Sales Coach'], ['/onboarding', 'Onboarding'], ['/settings', 'Settings'], ['/high-intent', 'High-Intent Leads']];
export const surfaceFor = (path) => (SURFACES.find(([p]) => path.startsWith(p)) || [])[1];

api.interceptors.request.use((config) => {
  const surface = surfaceFor(window.location.pathname || '');
  if (surface && !config.headers['X-Veluro-Surface']) config.headers['X-Veluro-Surface'] = surface;
  // Never override an explicitly-set Authorization header (e.g., admin portal calls)
  if (config.headers.Authorization) return config;
  const onAdminRoute = window.location.pathname.startsWith('/admin');
  const token = onAdminRoute
    ? (localStorage.getItem('admin_token') || localStorage.getItem('auth_token'))
    : localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // FastAPI validation errors arrive as an array of objects — flatten so toasts never try to render an object.
    if (Array.isArray(error.response?.data?.detail)) {
      error.response.data.detail = error.response.data.detail.map((d) => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d))).join('; ');
    }
    if (error.response?.status === 402 && typeof error.response?.data?.detail === 'string' && error.response.data.detail.includes('AI credits')) {
      window.dispatchEvent(new CustomEvent('llm-credits-402', { detail: error.response.data.detail }));
    }
    // Trial / subscription entitlement gate (structured 402 from the entitlement engine)
    if (error.response?.status === 402 && error.response?.data?.detail && typeof error.response.data.detail === 'object') {
      const d = error.response.data.detail;
      if (['trial_expired', 'subscription_inactive', 'partner_payment_pending'].includes(d.code)) {
        window.dispatchEvent(new CustomEvent('veluro-locked', { detail: d }));
      } else if (['trial_limit', 'upgrade_required'].includes(d.code)) {
        window.dispatchEvent(new CustomEvent('veluro-upgrade', { detail: d }));
      }
    }
    if (error.response?.status === 400 && typeof error.response?.data?.detail === 'string'
        && /connect .*(account|ads).*(settings)/i.test(error.response.data.detail)) {
      import('sonner').then(({ toast }) => {
        toast.error(error.response.data.detail, {
          action: { label: 'Connect now', onClick: () => window.location.assign('/settings?tab=integrations') },
          duration: 8000,
        });
      });
    }
    if (error.response?.status === 401) {
      const path = window.location.pathname;
      if (path.startsWith('/admin')) {
        localStorage.removeItem('admin_token');
        return Promise.reject(error);
      }
      const hadToken = !!localStorage.getItem('auth_token');
      localStorage.removeItem('auth_token');
      // The startup session check (/auth/me) never redirects by itself: the route decides — ProtectedRoute sends the owner to
      // login (with a return-to), PartnerEntry sends returning partners to login, and public pages (home, pricing, a partner's
      // vanity link veluro.ai/CODE, invite links…) simply render for a signed-out visitor. Redirecting here used to bounce a partner
      // testing his own referral link (expired token in his browser) to the login page — "my link doesn't work".
      const isSessionCheck = String(error.config?.url || '').includes('/auth/me');
      // Public routes that should NOT redirect to login on 401
      const isPublic = path === '/' || path === '/partner' || path.includes('/login') || path.includes('/signup') || path.startsWith('/join-team') || path.startsWith('/partner/join') || path.startsWith('/partners') || path.startsWith('/aspirepartners') || path.startsWith('/arc') || path.startsWith('/bluecoast') || path.startsWith('/platinumpartners') || path.startsWith('/veluropartner') || path.startsWith('/resellpartners') || path.includes('/book/') || path.includes('/booking/manage/') || path.startsWith('/admin') || path.startsWith('/cashclub') || path.startsWith('/snapshot/') || path.startsWith('/dm/') || path.startsWith('/privacy') || path.startsWith('/policy/') || path.startsWith('/terms') || path.startsWith('/data-deletion') || path.startsWith('/pricing') || path.startsWith('/demo') || path.startsWith('/compare') || path.startsWith('/try') || path.startsWith('/p/') || path.startsWith('/b/') || path.startsWith('/f/') || path.startsWith('/sign/');
      // Anonymous visitor on a single-segment path = likely a vanity referral link (veluro.ai/CODE)
      const isVanityVisitor = !hadToken && /^\/[A-Za-z0-9_-]{2,24}$/.test(path);
      if (!isSessionCheck && !isPublic && !isVanityVisitor && !window.location.hash?.includes('session_id=')) {
        // A session that ran out mid-use: back to login, then straight back to this screen.
        window.location.href = hadToken ? `/login?next=${encodeURIComponent(path + window.location.search)}&expired=1` : '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
